import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Button,
  Table,
  Badge,
  Row,
  Col,
  Form,
  Alert,
  Modal,
  Spinner,
} from "react-bootstrap";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  getFirestore,
  updateDoc,
} from "firebase/firestore";
import { FaUpload, FaTrash, FaFileExcel, FaPlus, FaDownload } from "react-icons/fa";
import Swal from "sweetalert2";
import firebaseApp from "../../firebase/credenciales";
import * as XLSX from "xlsx";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const db = getFirestore(firebaseApp);

const AdelantoSalario = ({ usuario }) => {
  const [adelantos, setAdelantos] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [adelantoIndividual, setAdelantoIndividual] = useState({
    empleado_id: "",
    monto: "",
    fecha: format(new Date(), "yyyy-MM-dd"),
    descripcion: "",
  });

  useEffect(() => {
    cargarEmpleados();
    cargarAdelantos();
  }, [usuario.ruc]);

  const cargarEmpleados = async () => {
    try {
      const q = query(
        collection(db, "pl_empleados"),
        where("ruc_empresa", "==", usuario.ruc),
        where("activo", "==", true)
      );
      const querySnapshot = await getDocs(q);
      const empleadosData = [];
      querySnapshot.forEach((doc) => {
        empleadosData.push({ id: doc.id, ...doc.data() });
      });
      setEmpleados(empleadosData);
    } catch (error) {
      console.error("Error al cargar empleados:", error);
      Swal.fire("Error", "No se pudieron cargar los empleados", "error");
    }
  };

  const cargarAdelantos = async () => {
    try {
      const q = query(
        collection(db, "pl_adelantos"),
        where("ruc_empresa", "==", usuario.ruc)
      );
      const querySnapshot = await getDocs(q);
      const adelantosData = [];
      querySnapshot.forEach((doc) => {
        adelantosData.push({ id: doc.id, ...doc.data() });
      });
      // Ordenar por fecha descendente
      adelantosData.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
      setAdelantos(adelantosData);
    } catch (error) {
      console.error("Error al cargar adelantos:", error);
      Swal.fire("Error", "No se pudieron cargar los adelantos", "error");
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        Swal.fire("Error", "El archivo Excel está vacío", "error");
        return;
      }

      // Validar formato del Excel
      const requiredColumns = ["DNI", "MONTO", "FECHA"];
      const firstRow = jsonData[0];
      const columnsPresent = requiredColumns.every(col => col in firstRow);

      if (!columnsPresent) {
        Swal.fire("Error", `El Excel debe contener las columnas: ${requiredColumns.join(", ")}`, "error");
        return;
      }

      const adelantosNuevos = [];
      const errores = [];

      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i];
        const dni = row.DNI?.toString().trim();
        const monto = parseFloat(row.MONTO);
        const fecha = row.FECHA;
        const descripcion = row.DESCRIPCION || `Adelanto ${format(new Date(), "dd/MM/yyyy")}`;

        // Validar empleado
        const empleado = empleados.find(e => e.dni === dni);
        if (!empleado) {
          errores.push(`Fila ${i + 2}: DNI ${dni} no encontrado`);
          continue;
        }

        // Validar monto
        if (isNaN(monto) || monto <= 0) {
          errores.push(`Fila ${i + 2}: Monto inválido (${row.MONTO})`);
          continue;
        }

        // Validar fecha
        let fechaValida;
        if (fecha instanceof Date) {
          fechaValida = fecha;
        } else if (typeof fecha === "number") {
          // Excel date serial number
          fechaValida = new Date((fecha - 25569) * 86400 * 1000);
        } else {
          fechaValida = new Date(fecha);
        }

        if (isNaN(fechaValida.getTime())) {
          errores.push(`Fila ${i + 2}: Fecha inválida (${fecha})`);
          continue;
        }

        adelantosNuevos.push({
          empleado_id: empleado.id,
          empleado_dni: dni,
          empleado_nombres: `${empleado.nombres} ${empleado.apellidos}`,
          monto: monto,
          fecha: format(fechaValida, "yyyy-MM-dd"),
          descripcion: descripcion,
          ruc_empresa: usuario.ruc,
          estado: "POR DESCONTAR", // POR DESCONTAR, DESCONTADO, CANCELADO
          created_at: new Date(),
          updated_at: new Date(),
        });
      }

      if (errores.length > 0) {
        Swal.fire("Errores encontrados", errores.join("\n"), "warning");
        return;
      }

      // Guardar adelantos en Firestore
      const promises = adelantosNuevos.map(async (adelanto) => {
        const docId = `${usuario.ruc}_${adelanto.empleado_id}_${Date.now()}`;
        await setDoc(doc(db, "pl_adelantos", docId), adelanto);
      });

      await Promise.all(promises);

      Swal.fire("Éxito", `Se cargaron ${adelantosNuevos.length} adelantos correctamente`, "success");
      cargarAdelantos(); // Recargar lista

    } catch (error) {
      console.error("Error al procesar archivo:", error);
      Swal.fire("Error", "Error al procesar el archivo Excel", "error");
    } finally {
      setLoading(false);
      // Limpiar input file
      event.target.value = "";
    }
  };

  const guardarAdelantoIndividual = async () => {
    if (!adelantoIndividual.empleado_id || !adelantoIndividual.monto || !adelantoIndividual.fecha) {
      Swal.fire("Error", "Complete todos los campos obligatorios", "error");
      return;
    }

    const monto = parseFloat(adelantoIndividual.monto);
    if (isNaN(monto) || monto <= 0) {
      Swal.fire("Error", "El monto debe ser un número positivo", "error");
      return;
    }

    setLoading(true);
    try {
      const empleado = empleados.find(e => e.id === adelantoIndividual.empleado_id);
      const adelantoData = {
        empleado_id: adelantoIndividual.empleado_id,
        empleado_dni: empleado.dni,
        empleado_nombres: `${empleado.nombres} ${empleado.apellidos}`,
        monto: monto,
        fecha: adelantoIndividual.fecha,
        descripcion: adelantoIndividual.descripcion || `Adelanto ${format(new Date(adelantoIndividual.fecha), "dd/MM/yyyy")}`,
        ruc_empresa: usuario.ruc,
        estado: "POR DESCONTAR",
        created_at: new Date(),
        updated_at: new Date(),
      };

      const docId = `${usuario.ruc}_${adelantoIndividual.empleado_id}_${Date.now()}`;
      await setDoc(doc(db, "pl_adelantos", docId), adelantoData);

      Swal.fire("Éxito", "Adelanto guardado correctamente", "success");
      setShowModal(false);
      setAdelantoIndividual({
        empleado_id: "",
        monto: "",
        fecha: format(new Date(), "yyyy-MM-dd"),
        descripcion: "",
      });
      cargarAdelantos();

    } catch (error) {
      console.error("Error al guardar adelanto:", error);
      Swal.fire("Error", "Error al guardar el adelanto", "error");
    } finally {
      setLoading(false);
    }
  };

  const eliminarAdelanto = async (id) => {
    const result = await Swal.fire({
      title: "¿Eliminar adelanto?",
      text: "Esta acción no se puede deshacer",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar"
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, "pl_adelantos", id));
        Swal.fire("Eliminado", "El adelanto ha sido eliminado", "success");
        cargarAdelantos();
      } catch (error) {
        console.error("Error al eliminar adelanto:", error);
        Swal.fire("Error", "Error al eliminar el adelanto", "error");
      }
    }
  };

  const cambiarEstadoAdelanto = async (id, nuevoEstado) => {
    try {
      await updateDoc(doc(db, "pl_adelantos", id), {
        estado: nuevoEstado,
        updated_at: new Date()
      });
      cargarAdelantos();
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      Swal.fire("Error", "Error al cambiar el estado del adelanto", "error");
    }
  };

  const exportarAdelantos = () => {
    const dataToExport = adelantos.map(adelanto => ({
      DNI: adelanto.empleado_dni,
      "Nombres y Apellidos": adelanto.empleado_nombres,
      Monto: adelanto.monto,
      Fecha: format(new Date(adelanto.fecha), "dd/MM/yyyy"),
      Descripción: adelanto.descripcion,
      Estado: adelanto.estado,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Adelantos");
    XLSX.writeFile(workbook, `adelantos_${usuario.ruc}_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
  };

  const generarPlantillaExcel = () => {
    // Crear datos de ejemplo para la plantilla
    const plantillaData = [
      {
        DNI: "12345678", // Ejemplo de DNI
        MONTO: 500.00, // Ejemplo de monto
        FECHA: format(new Date(), "dd/MM/yyyy"), // Fecha actual como ejemplo
        DESCRIPCION: "Adelanto de salario", // Descripción de ejemplo
      },
      // Puedes agregar más filas de ejemplo si lo deseas
    ];

    // Crear worksheet
    const worksheet = XLSX.utils.json_to_sheet(plantillaData);

    // Agregar fila de encabezados si no está presente (aunque json_to_sheet lo hace automáticamente)
    // Opcional: ajustar anchos de columna
    worksheet['!cols'] = [
      { wch: 10 }, // DNI
      { wch: 12 }, // MONTO
      { wch: 12 }, // FECHA
      { wch: 30 }, // DESCRIPCION
    ];

    // Crear workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Plantilla_Adelantos");

    // Descargar archivo
    XLSX.writeFile(workbook, `plantilla_adelantos_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
  };

  const getBadgeVariant = (estado) => {
    switch (estado) {
      case "POR DESCONTAR": return "warning";
      case "PENDIENTE": return "warning";
      case "DESCONTADO": return "success";
      case "APLICADO": return "success";
      case "CANCELADO": return "danger";
      default: return "secondary";
    }
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Indicador de carga */}
        {loading && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
            style={{ zIndex: 1080 }}
          >
            <div className="text-center">
              <Spinner
                animation="border"
                variant="light"
                style={{ width: "3rem", height: "3rem" }}
              />
              <p className="text-white mt-3 mb-0">
                Procesando...
              </p>
            </div>
          </div>
        )}

        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium */}
          <Card.Header
            className="text-white py-4"
            style={{
              background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
              borderBottom: "3px solid #D2691E",
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="fw-bold mb-1">
                  <FaUpload className="me-3" />
                  Gestión de Adelantos de Salario
                </h2>
                <p className="mb-0 opacity-75">
                  Administra los adelantos de salario para tus empleados
                </p>
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant="light"
                  onClick={generarPlantillaExcel}
                  style={{
                    borderRadius: "10px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                  className="d-flex align-items-center gap-2"
                >
                  <FaDownload size={16} />
                  Plantilla
                </Button>
                <Button
                  variant="light"
                  onClick={exportarAdelantos}
                  disabled={adelantos.length === 0}
                  style={{
                    borderRadius: "10px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                  className="d-flex align-items-center gap-2"
                >
                  <FaFileExcel size={16} />
                  Exportar
                </Button>
                <Button
                  variant="success"
                  onClick={() => setShowModal(true)}
                  style={{
                    borderRadius: "10px",
                    background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                    border: "none",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                  }}
                  className="d-flex align-items-center gap-2"
                >
                  <FaPlus size={16} />
                  Nuevo Adelanto
                </Button>
              </div>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Alert informativo */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Alert variant="info" className="mb-0" style={{ borderRadius: "10px", border: "none" }}>
                  <h6 className="fw-semibold">📋 Información sobre Adelantos</h6>
                  <p className="mb-2">
                    Los adelantos de salario se descuentan automáticamente en la planilla mensual.
                    Solo se aplican adelantos con estado "POR DESCONTAR".
                  </p>
                  <strong>Formato Excel requerido:</strong>
                  <ul className="mb-0 mt-2">
                    <li><strong>DNI:</strong> Número de documento del empleado</li>
                    <li><strong>MONTO:</strong> Monto del adelanto (número positivo)</li>
                    <li><strong>FECHA:</strong> Fecha del adelanto (DD/MM/YYYY o formato Excel)</li>
                    <li><strong>DESCRIPCION:</strong> Descripción opcional del adelanto</li>
                  </ul>
                </Alert>
              </Card.Body>
            </Card>

            {/* Carga masiva */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Header
                className="text-white py-3"
                style={{
                  background: "linear-gradient(135deg, #D2691E 0%, #CD853F 100%)",
                  borderRadius: "15px 15px 0 0",
                }}
              >
                <h5 className="mb-0 fw-semibold">
                  <FaUpload className="me-2" />
                  Carga Masiva desde Excel
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Row>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                        Seleccione archivo Excel
                      </Form.Label>
                      <Form.Control
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileUpload}
                        disabled={loading}
                        style={{ borderRadius: "10px" }}
                      />
                      <Form.Text className="text-muted">
                        Seleccione un archivo Excel con los adelantos a cargar
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Lista de adelantos */}
            <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
              <Card.Header
                className="text-white py-3"
                style={{
                  background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                  borderRadius: "15px 15px 0 0",
                }}
              >
                <h5 className="mb-0 fw-semibold">
                  Lista de Adelantos ({adelantos.length})
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
                {adelantos.length === 0 ? (
                  <div className="text-center py-5">
                    <FaUpload size={48} className="text-muted mb-3" />
                    <h5>No hay adelantos registrados</h5>
                    <p className="text-muted">Comienza registrando un adelanto individual o cargando desde Excel</p>
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <Table hover className="mb-0">
                      <thead style={{ backgroundColor: "#f8f9fa" }}>
                        <tr>
                          <th className="border-0 py-3 px-3">DNI</th>
                          <th className="border-0 py-3 px-3">Empleado</th>
                          <th className="border-0 py-3 px-3 text-end">Monto</th>
                          <th className="border-0 py-3 px-3">Fecha</th>
                          <th className="border-0 py-3 px-3">Descripción</th>
                          <th className="border-0 py-3 px-3">Estado</th>
                          <th className="border-0 py-3 px-3 text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adelantos.map((adelanto) => (
                          <tr key={adelanto.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                            <td className="py-3 px-3">
                              <code className="text-muted">{adelanto.empleado_dni}</code>
                            </td>
                            <td className="py-3 px-3">{adelanto.empleado_nombres}</td>
                            <td className="py-3 px-3 text-end">
                              <span className="fw-bold text-success">
                                S/ {adelanto.monto.toFixed(2)}
                              </span>
                            </td>
                            <td className="py-3 px-3">
                              {format(new Date(adelanto.fecha), "dd/MM/yyyy", { locale: es })}
                            </td>
                            <td className="py-3 px-3">{adelanto.descripcion}</td>
                            <td className="py-3 px-3">
                              <Badge
                                bg={getBadgeVariant(adelanto.estado)}
                                className="px-3 py-2"
                                style={{ fontSize: "0.75rem" }}
                              >
                                {adelanto.estado}
                              </Badge>
                            </td>
                             <td className="py-3 px-3 text-center">
                               <div className="d-flex gap-1 justify-content-center">
                                 {(adelanto.estado === "POR DESCONTAR" || adelanto.estado === "PENDIENTE") && (
                                   <>
                                     <Button
                                       variant="outline-warning"
                                       size="sm"
                                       onClick={() => cambiarEstadoAdelanto(adelanto.id, "CANCELADO")}
                                       title="Cancelar adelanto"
                                       style={{ borderRadius: "8px" }}
                                     >
                                       ✗
                                     </Button>
                                     <Button
                                       variant="outline-danger"
                                       size="sm"
                                       onClick={() => eliminarAdelanto(adelanto.id)}
                                       title="Eliminar adelanto"
                                       style={{ borderRadius: "8px" }}
                                     >
                                       <FaTrash />
                                     </Button>
                                   </>
                                 )}
                               </div>
                             </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>

        {/* Modal para adelanto individual */}
        <Modal
          show={showModal}
          onHide={() => setShowModal(false)}
          centered
          size="lg"
        >
          <Modal.Header
            closeButton
            style={{
              background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
              color: "white",
            }}
          >
            <Modal.Title className="fw-bold">
              <FaPlus className="me-2" />
              Registrar Adelanto Individual
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                      Empleado *
                    </Form.Label>
                    <Form.Select
                      value={adelantoIndividual.empleado_id}
                      onChange={(e) => setAdelantoIndividual({
                        ...adelantoIndividual,
                        empleado_id: e.target.value
                      })}
                      style={{ borderRadius: "10px" }}
                    >
                      <option value="">Seleccionar empleado</option>
                      {empleados.map((empleado) => (
                        <option key={empleado.id} value={empleado.id}>
                          {empleado.dni} - {empleado.nombres} {empleado.apellidos}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                      Monto *
                    </Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={adelantoIndividual.monto}
                      onChange={(e) => setAdelantoIndividual({
                        ...adelantoIndividual,
                        monto: e.target.value
                      })}
                      style={{ borderRadius: "10px" }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                      Fecha *
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={adelantoIndividual.fecha}
                      onChange={(e) => setAdelantoIndividual({
                        ...adelantoIndividual,
                        fecha: e.target.value
                      })}
                      style={{ borderRadius: "10px" }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                      Descripción
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Descripción del adelanto"
                      value={adelantoIndividual.descripcion}
                      onChange={(e) => setAdelantoIndividual({
                        ...adelantoIndividual,
                        descripcion: e.target.value
                      })}
                      style={{ borderRadius: "10px" }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowModal(false)}
              style={{ borderRadius: "10px" }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={guardarAdelantoIndividual}
              disabled={loading}
              style={{
                borderRadius: "10px",
                background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                border: "none",
              }}
            >
              {loading ? "Guardando..." : "Guardar Adelanto"}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default AdelantoSalario;